/**
 * Dashboard → Costs. Where LLM spend goes, and who it is spent on.
 *
 * Owns its own time range (1d / 7d / 4w) rather than using the page-level one,
 * because `llm-metrics` has a 30-day TTL — the dashboard's 8-week and All
 * ranges have no data behind them. The audience toggle still comes from the
 * page, so this tab counts the same population as every other.
 *
 * Two things the numbers deliberately show rather than hide:
 *   - Spend with no user attached (admin/system work — extraction, enrichment)
 *     is its own tile, so the tables reconcile to the total.
 *   - A student enrolled with several teachers has their cost split evenly
 *     between them, so the teacher column sums to the student total.
 *
 * Palette is validated for both themes (OKLCH lightness band, chroma floor,
 * CVD separation, contrast) — the worst adjacent pair sits in the 6–8 ΔE band,
 * which is legal only alongside secondary encoding, hence the 2px surface gaps
 * between stacked segments, the always-present legend, and the tables below.
 *
 * See docs/FeatureDesigns/PipelineInfra/llm-cost-attribution.md.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from 'recharts';
import { DataTable, type ColumnDef } from '@shared-ui/components';
import { adminApi, type CostAnalyticsResponse } from '../../api/adminApi';
import './CostsTab.css';

/** Validated categorical order — do not reorder or cycle. Assigned by series
 *  identity, never by rank, so filtering never repaints the survivors. */
const SERIES = ['#3b82f6', '#d97706', '#059669', '#ec4899', '#0891b2', '#8b5cf6'];
const OTHER = 'var(--text-tertiary, #9ca3af)';

/** Colour follows the entity. Anything past the 6th slot folds into "Other"
 *  rather than generating a 7th hue. */
function colorFor(index: number): string {
  return index < SERIES.length ? SERIES[index] : OTHER;
}

const RANGES = [
  { value: 1, label: '1 Day' },
  { value: 7, label: '7 Days' },
  { value: 28, label: '4 Weeks' },
];

const usd = (n: number) => `$${n.toFixed(n >= 1 ? 2 : 4)}`;
const usdShort = (n: number) => (n >= 1 ? `$${n.toFixed(2)}` : `$${n.toFixed(3)}`);

/** Top N keys by spend; everything else collapses into one "Other" series. */
function topKeys(rows: { key: string; usd: number }[], n: number): string[] {
  return rows.slice(0, n).map(r => r.key);
}

interface Props {
  includeAll: boolean;
}

export function CostsTab({ includeAll }: Props) {
  const [days, setDays] = useState(7);
  const [stackBy, setStackBy] = useState<'operation' | 'bucket'>('operation');

  const { data, isLoading, error } = useQuery({
    queryKey: ['costAnalytics', days, includeAll],
    queryFn: () => adminApi.getCostAnalytics({ days, includeAll }),
  });

  if (isLoading) return <div className="costs-loading">Loading…</div>;
  if (error) return <div className="costs-error">Failed to load cost data.</div>;
  if (!data) return null;

  return (
    <div className="costs-tab">
      <RangePicker days={days} onChange={setDays} maxDays={data.period.maxDays} />
      <SummaryTiles data={data} />
      <DailyChart data={data} stackBy={stackBy} onStackByChange={setStackBy} />
      <div className="costs-row">
        <BreakdownChart title="Spend by operation" slices={data.byOperation} />
        <BreakdownChart title="Spend by model" slices={data.byProvider} />
      </div>
      <BucketChart data={data} />
      <StudentTable data={data} />
      <TeacherTable data={data} />
    </div>
  );
}

// ── Controls ────────────────────────────────────────────────────────────

function RangePicker({ days, onChange, maxDays }: { days: number; onChange: (d: number) => void; maxDays: number }) {
  return (
    <div className="costs-controls">
      <div className="time-toggle">
        {RANGES.map(r => (
          <button
            key={r.value}
            className={`time-toggle-btn ${days === r.value ? 'active' : ''}`}
            onClick={() => onChange(r.value)}
          >
            {r.label}
          </button>
        ))}
      </div>
      <span className="costs-note">
        Capped at {maxDays} days — per-call metrics are retained for 30.
      </span>
    </div>
  );
}

// ── Summary ─────────────────────────────────────────────────────────────

function SummaryTiles({ data }: { data: CostAnalyticsResponse }) {
  const s = data.summary;
  const unattributedPct = s.totalUsd ? Math.round((s.unattributedUsd / s.totalUsd) * 100) : 0;
  const tiles = [
    { label: 'Total spend', value: usd(s.totalUsd), sub: `${s.calls.toLocaleString()} calls` },
    { label: 'Attributed to users', value: usd(s.attributedUsd), sub: `${s.activeUsers} active users` },
    { label: 'Unattributed', value: usd(s.unattributedUsd), sub: `${unattributedPct}% — admin & system work` },
    { label: 'Cost per active user', value: usd(s.costPerActiveUser), sub: `over ${data.period.days}d` },
  ];
  return (
    <div className="costs-tiles">
      {tiles.map(t => (
        <div className="costs-tile" key={t.label}>
          <div className="costs-tile-label">{t.label}</div>
          <div className="costs-tile-value">{t.value}</div>
          <div className="costs-tile-sub">{t.sub}</div>
        </div>
      ))}
    </div>
  );
}

// ── Charts ──────────────────────────────────────────────────────────────

const axisProps = {
  stroke: 'var(--text-tertiary, #9ca3af)',
  fontSize: 12,
  tickLine: false,
} as const;

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const rows = [...payload].filter(p => p.value > 0).sort((a, b) => b.value - a.value);
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <div className="costs-tooltip">
      <div className="costs-tooltip-title">{label}</div>
      {rows.map(r => (
        <div className="costs-tooltip-row" key={r.dataKey}>
          <span className="costs-swatch" style={{ background: r.color }} />
          <span className="costs-tooltip-key">{r.dataKey}</span>
          <span className="costs-tooltip-val">{usdShort(r.value)}</span>
        </div>
      ))}
      {rows.length > 1 && (
        <div className="costs-tooltip-total"><span>Total</span><span>{usdShort(total)}</span></div>
      )}
    </div>
  );
}

function DailyChart({ data, stackBy, onStackByChange }: {
  data: CostAnalyticsResponse;
  stackBy: 'operation' | 'bucket';
  onStackByChange: (v: 'operation' | 'bucket') => void;
}) {
  const slices = stackBy === 'operation' ? data.byOperation : data.byBucket;
  const keys = topKeys(slices, SERIES.length);
  const field = stackBy === 'operation' ? 'byOperation' : 'byBucket';

  const rows = data.daily.map(d => {
    const src = d[field] as Record<string, number>;
    const row: Record<string, string | number> = { date: d.date.slice(5) };
    let other = 0;
    for (const [k, v] of Object.entries(src)) {
      if (keys.includes(k)) row[k] = v; else other += v;
    }
    if (other > 0) row.Other = other;
    return row;
  });
  const stackKeys = [...keys, ...(rows.some(r => r.Other) ? ['Other'] : [])];

  return (
    <section className="costs-card">
      <header className="costs-card-head">
        <h3>Daily spend</h3>
        <div className="time-toggle small">
          <button className={`time-toggle-btn ${stackBy === 'operation' ? 'active' : ''}`}
            onClick={() => onStackByChange('operation')}>By operation</button>
          <button className={`time-toggle-btn ${stackBy === 'bucket' ? 'active' : ''}`}
            onClick={() => onStackByChange('bucket')}>By cost bucket</button>
        </div>
      </header>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
          <XAxis dataKey="date" {...axisProps} axisLine={false} />
          <YAxis {...axisProps} axisLine={false} tickFormatter={(v: number) => usdShort(v)} width={64} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--bg-tertiary, #f3f4f6)', opacity: 0.4 }} />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
          {stackKeys.map((k, i) => (
            <Bar
              key={k} dataKey={k} stackId="spend"
              fill={k === 'Other' ? OTHER : colorFor(i)}
              /* 2px surface gap between segments — the secondary encoding the
                 CVD check requires, and it reads better besides. */
              stroke="var(--bg-secondary, #ffffff)" strokeWidth={2}
              radius={i === stackKeys.length - 1 ? [4, 4, 0, 0] : 0}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </section>
  );
}

/** Single-series magnitude — one hue, no legend (the title names it). */
function BreakdownChart({ title, slices }: { title: string; slices: { key: string; usd: number; calls: number }[] }) {
  const rows = slices.slice(0, 8).map(s => ({ name: s.key, usd: s.usd, calls: s.calls }));
  return (
    <section className="costs-card">
      <header className="costs-card-head"><h3>{title}</h3></header>
      <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 34 + 40)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 56, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" horizontal={false} />
          <XAxis type="number" {...axisProps} axisLine={false} tickFormatter={(v: number) => usdShort(v)} />
          <YAxis type="category" dataKey="name" {...axisProps} axisLine={false} width={150} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--bg-tertiary, #f3f4f6)', opacity: 0.4 }} />
          <Bar dataKey="usd" fill={SERIES[0]} radius={[0, 4, 4, 0]} barSize={16} />
        </BarChart>
      </ResponsiveContainer>
    </section>
  );
}

function BucketChart({ data }: { data: CostAnalyticsResponse }) {
  const rows = data.byBucket.map(b => ({ name: b.key, usd: b.usd, calls: b.calls }));
  const onlyUnknown = rows.length === 1 && rows[0].name === 'unknown';
  return (
    <section className="costs-card">
      <header className="costs-card-head">
        <h3>Spend by cost bucket</h3>
        {onlyUnknown && (
          <span className="costs-note">
            No bucket recorded yet — rows are stamped only by a build carrying the attribution change.
          </span>
        )}
      </header>
      <ResponsiveContainer width="100%" height={Math.max(160, rows.length * 34 + 40)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 56, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" horizontal={false} />
          <XAxis type="number" {...axisProps} axisLine={false} tickFormatter={(v: number) => usdShort(v)} />
          <YAxis type="category" dataKey="name" {...axisProps} axisLine={false} width={110} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--bg-tertiary, #f3f4f6)', opacity: 0.4 }} />
          <Bar dataKey="usd" radius={[0, 4, 4, 0]} barSize={16}>
            {rows.map((r, i) => <Cell key={r.name} fill={r.name === 'unknown' ? OTHER : colorFor(i)} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </section>
  );
}

// ── Tables ──────────────────────────────────────────────────────────────

function StudentTable({ data }: { data: CostAnalyticsResponse }) {
  const columns: ColumnDef<CostAnalyticsResponse['students'][number]>[] = [
    { key: 'name', header: 'Student', mobile: 'title', render: r => (
      <div><div>{r.name}</div><div className="costs-sub">{r.email}</div></div>
    ) },
    { key: 'usd', header: 'Spend', className: 'costs-num', render: r => usd(r.usd) },
    { key: 'calls', header: 'Calls', className: 'costs-num', render: r => r.calls.toLocaleString() },
    { key: 'topOperation', header: 'Top operation', render: r => r.topOperation || '—' },
    { key: 'teacherCount', header: 'Teachers', className: 'costs-num', render: r => String(r.teacherCount || '—') },
  ];
  return (
    <section className="costs-card">
      <header className="costs-card-head">
        <h3>Cost by student</h3>
        <span className="costs-note">{data.students.length} users with attributable spend</span>
      </header>
      <DataTable columns={columns} data={data.students} rowKey={r => r.userId} />
    </section>
  );
}

function TeacherTable({ data }: { data: CostAnalyticsResponse }) {
  const columns: ColumnDef<CostAnalyticsResponse['teachers'][number]>[] = [
    { key: 'name', header: 'Teacher', mobile: 'title', render: r => r.name },
    { key: 'studentCount', header: 'Students', className: 'costs-num', render: r => String(r.studentCount) },
    { key: 'ownUsd', header: 'Own spend', className: 'costs-num', render: r => usd(r.ownUsd) },
    { key: 'studentsUsd', header: "Students' spend", className: 'costs-num', render: r => usd(r.studentsUsd) },
    { key: 'totalUsd', header: 'Total', className: 'costs-num', render: r => <strong>{usd(r.totalUsd)}</strong> },
  ];
  return (
    <section className="costs-card">
      <header className="costs-card-head">
        <h3>Cost by teacher</h3>
        <span className="costs-note">
          A student enrolled with several teachers has their spend split evenly between them,
          so this column sums to the student total.
        </span>
      </header>
      <DataTable columns={columns} data={data.teachers} rowKey={r => r.teacherId} />
    </section>
  );
}
